/**
 * Bank payment affordability helpers for wallet-to-bank EFT and Instant (PayShap).
 * Fees come from the quote API; these utilities only handle balance math.
 */

export const MIN_BANK_PAYMENT_AMOUNT_ZAR = 1;

function roundMoney(value: number): number {
  return Number(Number(value || 0).toFixed(2));
}

/**
 * Parse wallet balance from API number or formatted string (e.g. "R1,234.56").
 */
export function parseWalletBalanceZar(formattedOrNumber: string | number | null | undefined): number {
  if (formattedOrNumber === null || formattedOrNumber === undefined) return 0;
  if (typeof formattedOrNumber === 'number') {
    return Number.isFinite(formattedOrNumber) ? roundMoney(formattedOrNumber) : 0;
  }
  const cleaned = String(formattedOrNumber).replace(/[R\s,]/g, '');
  const parsed = parseFloat(cleaned);
  return Number.isFinite(parsed) ? roundMoney(parsed) : 0;
}

/**
 * Maximum payment amount the user can send after deducting the transaction fee.
 */
export function computeMaxAffordableAmount(availableBalance: number, feeAmount: number): number {
  const balance = roundMoney(availableBalance);
  const fee = roundMoney(feeAmount);
  return roundMoney(Math.max(balance - fee, 0));
}

/**
 * True when total debit (amount + fee) exceeds available wallet balance.
 */
export function isOverAffordableLimit(
  _amount: number,
  totalDebit: number,
  availableBalance: number,
): boolean {
  return roundMoney(totalDebit) > roundMoney(availableBalance);
}

/**
 * Whether the user can proceed with a bank payment after fee adjustment.
 */
export function canProceedWithBankPayment(maxAffordable: number): boolean {
  return maxAffordable >= MIN_BANK_PAYMENT_AMOUNT_ZAR;
}
